import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, Loader2, Search, RefreshCw, FileText, Clock, CheckCircle2,
  XCircle, Rocket, Upload, ExternalLink, Mail, DollarSign, Eye, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import LightBackground from '@/components/LightBackground';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Brief {
  id: string;
  user_id: string | null;
  full_name: string;
  email: string;
  project_name: string;
  industry: string;
  business_type: string;
  business_model: string | null;
  target_audience: string | null;
  description: string;
  primary_goal: string | null;
  investment_expected: string | null;
  budget_range: string | null;
  timeline: string;
  already_has: string | null;
  countries_targeted: string | null;
  languages: string | null;
  inspiration_links: string | null;
  competitors: string | null;
  faceless_preference: string | null;
  monetization_preference: string | null;
  preferred_platforms: string[] | null;
  additional_notes: string | null;
  has_content: boolean | null;
  has_audience: boolean | null;
  has_logo: boolean | null;
  has_domain: boolean | null;
  has_brand: boolean | null;
  review_status: string;
  decline_reason: string | null;
  admin_notes: string | null;
  invoice_url: string | null;
  quoted_amount: number | null;
  reviewed_at: string | null;
  created_at: string;
  invoice_line_items: InvoiceLineItem[] | null;
  invoice_billing_address: string | null;
  invoice_client_tax_id: string | null;
  invoice_payment_terms: string | null;
  invoice_tax_rate: number | null;
  invoice_discount: number | null;
  invoice_purchase_order: string | null;
}

interface InvoiceLineItem {
  description: string;
  detail?: string;
  quantity?: number;
  unit?: string;
  rate?: number;
  amount?: number;
}


const STATUS_META: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: <Clock className="w-3.5 h-3.5" /> },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  paid: { label: 'In Production', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: <Rocket className="w-3.5 h-3.5" /> },
  declined: { label: 'Declined', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: <XCircle className="w-3.5 h-3.5" /> },
};

const AdminLaunchKitBriefs = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Brief | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (role !== 'admin') { navigate('/dashboard'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role, authLoading]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('launch_kit_briefs')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) toast.error('Could not load briefs');
    else setBriefs((data || []) as unknown as Brief[]);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return briefs.filter((b) => {
      if (statusFilter !== 'all' && b.review_status !== statusFilter) return false;
      if (!q) return true;
      return (
        b.project_name.toLowerCase().includes(q) ||
        b.full_name.toLowerCase().includes(q) ||
        b.email.toLowerCase().includes(q) ||
        b.industry.toLowerCase().includes(q)
      );
    });
  }, [briefs, search, statusFilter]);

  const counts = useMemo(() => ({
    all: briefs.length,
    pending: briefs.filter((b) => b.review_status === 'pending').length,
    approved: briefs.filter((b) => b.review_status === 'approved').length,
    paid: briefs.filter((b) => b.review_status === 'paid').length,
    declined: briefs.filter((b) => b.review_status === 'declined').length,
  }), [briefs]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Launch Kit Briefs — Admin | Faceless Aura Club</title></Helmet>
      <div className="min-h-screen bg-slate-50 relative">
        <LightBackground />

        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Link to="/admin/crm" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600">
                <ArrowLeft className="w-4 h-4" /> Back to CRM
              </Link>
              <span className="text-slate-300">/</span>
              <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-blue-600" /> Launch Kit Briefs
              </h1>
            </div>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </header>

        <main className="relative z-10 max-w-7xl mx-auto px-6 py-8 space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Total" value={counts.all} cls="bg-slate-100 text-slate-700" />
            <Kpi label="Pending" value={counts.pending} cls="bg-amber-100 text-amber-700" />
            <Kpi label="Approved" value={counts.approved} cls="bg-green-100 text-green-700" />
            <Kpi label="In Production" value={counts.paid} cls="bg-blue-100 text-blue-700" />
            <Kpi label="Declined" value={counts.declined} cls="bg-rose-100 text-rose-700" />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center bg-white border border-slate-200 rounded-xl p-4">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by project, client, email, industry…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 mr-1.5" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">In Production</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Project</th>
                    <th className="text-left px-5 py-3">Client</th>
                    <th className="text-left px-5 py-3">Industry</th>
                    <th className="text-left px-5 py-3">Submitted</th>
                    <th className="text-left px-5 py-3">Quote</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-right px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-12 text-slate-500">No briefs found</td></tr>
                  )}
                  {filtered.map((b) => {
                    const s = STATUS_META[b.review_status] || STATUS_META.pending;
                    return (
                      <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                        <td className="px-5 py-3 font-medium text-slate-900">{b.project_name}</td>
                        <td className="px-5 py-3 text-slate-700">
                          <div>{b.full_name}</div>
                          <div className="text-xs text-slate-500">{b.email}</div>
                        </td>
                        <td className="px-5 py-3 text-slate-600">{b.industry}</td>
                        <td className="px-5 py-3 text-slate-600">{new Date(b.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-3 text-slate-700">{b.quoted_amount ? `$${Number(b.quoted_amount).toLocaleString()}` : '—'}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`}>
                            {s.icon}{s.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <Button size="sm" variant="outline" onClick={() => setSelected(b)}>
                            <Eye className="w-4 h-4 mr-1.5" /> Review
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </main>

        {selected && (
          <ReviewDialog
            brief={selected}
            onClose={() => setSelected(null)}
            onSaved={() => { setSelected(null); load(); }}
          />
        )}
      </div>
    </>
  );
};

const Kpi = ({ label, value, cls }: { label: string; value: number; cls: string }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">{label}</div>
    <div className="flex items-center gap-2 mt-1.5">
      <span className={`px-2 py-0.5 rounded-md text-2xl font-bold ${cls}`}>{value}</span>
    </div>
  </div>
);

const DEFAULT_LINE_ITEMS: InvoiceLineItem[] = [
  { description: 'Discovery, research & niche validation', detail: 'Market scan, competitor mapping, audience research, opportunity sizing.', quantity: 6, unit: 'hrs', rate: 95 },
  { description: 'Strategy, positioning & monetization plan', detail: 'Offer architecture, pricing model, revenue streams, growth loops.', quantity: 5, unit: 'hrs', rate: 95 },
  { description: 'Build, configuration & test run', detail: 'Tooling setup, automations, content scaffolding, QA pass.', quantity: 8, unit: 'hrs', rate: 95 },
  { description: 'Launch roadmap & 30-day playbook', detail: 'Step-by-step go-to-market plan with KPIs and review checkpoints.', quantity: 3, unit: 'hrs', rate: 95 },
];

const ReviewDialog = ({ brief, onClose, onSaved }: { brief: Brief; onClose: () => void; onSaved: () => void }) => {
  const [adminNotes, setAdminNotes] = useState(brief.admin_notes || '');
  const [declineReason, setDeclineReason] = useState(brief.decline_reason || '');
  const [invoiceUrl, setInvoiceUrl] = useState(brief.invoice_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState<'approve' | 'decline' | 'production' | null>(null);

  // Itemized invoice editor state
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(
    (brief.invoice_line_items && brief.invoice_line_items.length > 0)
      ? brief.invoice_line_items
      : DEFAULT_LINE_ITEMS,
  );
  const [billingAddress, setBillingAddress] = useState(brief.invoice_billing_address || '');
  const [clientTaxId, setClientTaxId] = useState(brief.invoice_client_tax_id || '');
  const [purchaseOrder, setPurchaseOrder] = useState(brief.invoice_purchase_order || '');
  const [paymentTerms, setPaymentTerms] = useState(
    brief.invoice_payment_terms ||
      'Payment due on receipt. Production scheduling begins once payment is confirmed. Refunds governed by the 7-day Risk Reversal as per the Launch Kit terms.',
  );
  const [taxRate, setTaxRate] = useState<string>(brief.invoice_tax_rate != null ? String(brief.invoice_tax_rate) : '0');
  const [discount, setDiscount] = useState<string>(brief.invoice_discount != null ? String(brief.invoice_discount) : '0');

  const lineTotal = (it: InvoiceLineItem): number => {
    if (it.amount != null && it.amount > 0) return Number(it.amount);
    const q = Number(it.quantity || 0);
    const r = Number(it.rate || 0);
    return +(q * r).toFixed(2);
  };
  const subtotal = lineItems.reduce((s, it) => s + lineTotal(it), 0);
  const discountNum = Math.max(0, parseFloat(discount) || 0);
  const taxNum = Math.max(0, parseFloat(taxRate) || 0);
  const taxedBase = Math.max(0, subtotal - discountNum);
  const taxAmount = +(taxedBase * (taxNum / 100)).toFixed(2);
  const grandTotal = +(taxedBase + taxAmount).toFixed(2);

  const updateItem = (idx: number, patch: Partial<InvoiceLineItem>) => {
    setLineItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx: number) => setLineItems((prev) => prev.filter((_, i) => i !== idx));
  const addItem = (preset?: InvoiceLineItem) => setLineItems((prev) => [...prev, preset || { description: '', quantity: 1, unit: 'hrs', rate: 95 }]);


  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'pdf';
      const path = `${brief.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('launch-kit-invoices')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      // Persist the storage path on the brief so authorized users can fetch a signed URL.
      const { error: updErr } = await supabase
        .from('launch_kit_briefs')
        .update({ invoice_storage_path: path })
        .eq('id', brief.id);
      if (updErr) throw updErr;
      setInvoiceUrl(`stored:${path}`);
      toast.success('Invoice uploaded — visible to the client via secure signed link');
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const update = async (patch: Partial<Brief>) => {
    const { error } = await supabase
      .from('launch_kit_briefs')
      .update({ ...(patch as any), reviewed_at: new Date().toISOString() })
      .eq('id', brief.id);
    if (error) throw error;
  };

  const handleApprove = async () => {
    if (grandTotal <= 0) { toast.error('Add at least one line item with an amount'); return; }
    const cleanItems = lineItems
      .map((it) => ({
        ...it,
        amount: lineTotal(it),
      }))
      .filter((it) => it.description.trim().length > 0 && it.amount > 0);
    if (cleanItems.length === 0) { toast.error('Add at least one line item'); return; }
    setSaving('approve');
    try {
      await update({
        review_status: 'approved',
        quoted_amount: grandTotal,
        admin_notes: adminNotes || null,
        invoice_url: invoiceUrl || null,
        decline_reason: null,
        invoice_line_items: cleanItems,
        invoice_billing_address: billingAddress || null,
        invoice_client_tax_id: clientTaxId || null,
        invoice_purchase_order: purchaseOrder || null,
        invoice_payment_terms: paymentTerms || null,
        invoice_tax_rate: taxNum,
        invoice_discount: discountNum,
      });
      const { error: invErr } = await supabase.functions.invoke('generate-launch-kit-invoice', {
        body: { brief_id: brief.id },
      });
      if (invErr) {
        toast.warning('Approved, but invoice PDF generation failed — you can retry from the brief.');
      } else {
        toast.success('Brief approved — detailed invoice PDF generated and emailed to the client');
      }
      onSaved();
    } catch (e: any) { toast.error(e?.message || 'Failed to approve'); }
    finally { setSaving(null); }
  };


  const handleDecline = async () => {
    if (!declineReason.trim()) { toast.error('Add a reason so the client understands'); return; }
    setSaving('decline');
    try {
      await update({
        review_status: 'declined',
        decline_reason: declineReason,
        admin_notes: adminNotes || null,
      });
      toast.success('Brief declined');
      onSaved();
    } catch (e: any) { toast.error(e?.message || 'Failed to decline'); }
    finally { setSaving(null); }
  };

  const handleMarkPaid = async () => {
    setSaving('production');
    try {
      await update({ review_status: 'paid' });
      toast.success('Marked as In Production');
      onSaved();
    } catch (e: any) { toast.error(e?.message || 'Failed'); }
    finally { setSaving(null); }
  };

  const F = ({ label, value, multiline }: { label: string; value?: string | null; multiline?: boolean }) =>
    value ? (
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-sm text-slate-800 ${multiline ? 'whitespace-pre-wrap' : ''}`}>{value}</p>
      </div>
    ) : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{brief.project_name}</DialogTitle>
          <DialogDescription>
            {brief.full_name} · {brief.email} · Submitted {new Date(brief.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 py-4">
          {/* Left: submitted brief */}
          <div className="space-y-4 pr-2 border-r-0 md:border-r border-slate-100">
            <h3 className="font-semibold text-slate-900">Submitted brief</h3>
            <F label="Industry" value={brief.industry} />
            <F label="Business type" value={brief.business_type} />
            <F label="Business model" value={brief.business_model} />
            <F label="Target audience" value={brief.target_audience} />
            <F label="Description" value={brief.description} multiline />
            <F label="Primary goal" value={brief.primary_goal} />
            <F label="Investment expected" value={brief.investment_expected} />
            <F label="Budget range" value={brief.budget_range} />
            <F label="Timeline" value={brief.timeline} />
            <F label="What client already has" value={brief.already_has} multiline />
            <F label="Countries" value={brief.countries_targeted} />
            <F label="Languages" value={brief.languages} />
            <F label="Faceless preference" value={brief.faceless_preference} />
            <F label="Monetization" value={brief.monetization_preference} />
            {brief.preferred_platforms?.length ? <F label="Platforms" value={brief.preferred_platforms.join(', ')} /> : null}
            <F label="Competitors" value={brief.competitors} multiline />
            <F label="Inspiration" value={brief.inspiration_links} multiline />
            <F label="Additional notes" value={brief.additional_notes} multiline />
            <div className="flex flex-wrap gap-2">
              {brief.has_logo && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Logo</span>}
              {brief.has_brand && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Brand</span>}
              {brief.has_domain && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Domain</span>}
              {brief.has_content && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Content</span>}
              {brief.has_audience && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Audience</span>}
            </div>
          </div>

          {/* Right: admin actions */}
          <div className="space-y-5">
            <h3 className="font-semibold text-slate-900">Decision &amp; invoice</h3>




            {/* Itemized invoice editor */}
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-900">Invoice line items</Label>
                <button
                  type="button"
                  onClick={() => addItem()}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  + Add line
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {DEFAULT_LINE_ITEMS.map((preset, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => addItem(preset)}
                    className="text-[10px] px-2 py-1 rounded-full bg-white border border-slate-200 hover:border-blue-400 text-slate-600 hover:text-blue-700"
                  >
                    + {preset.description.split(',')[0]}
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                {lineItems.map((it, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-2">
                    <div className="flex items-start gap-2">
                      <Input
                        className="text-sm h-8 flex-1"
                        placeholder="Line description (e.g. Build & test run)"
                        value={it.description}
                        onChange={(e) => updateItem(idx, { description: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="text-rose-500 hover:text-rose-600 text-xs px-1.5"
                        aria-label="Remove line"
                      >
                        ✕
                      </button>
                    </div>
                    <Textarea
                      rows={2}
                      className="text-xs"
                      placeholder="Detail (optional) — what is included in this line"
                      value={it.detail || ''}
                      onChange={(e) => updateItem(idx, { detail: e.target.value })}
                    />
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-500">Qty / Hours</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.25"
                          className="h-8 text-sm"
                          value={it.quantity ?? ''}
                          onChange={(e) => updateItem(idx, { quantity: e.target.value === '' ? undefined : Number(e.target.value) })}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-500">Unit</Label>
                        <Select
                          value={it.unit || 'hrs'}
                          onValueChange={(v) => updateItem(idx, { unit: v })}
                        >
                          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hrs">Hours</SelectItem>
                            <SelectItem value="day">Day(s)</SelectItem>
                            <SelectItem value="scope">Fixed scope</SelectItem>
                            <SelectItem value="pcs">Pieces</SelectItem>
                            <SelectItem value="mo">Month(s)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-500">Rate (USD)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          className="h-8 text-sm"
                          value={it.rate ?? ''}
                          onChange={(e) => updateItem(idx, { rate: e.target.value === '' ? undefined : Number(e.target.value) })}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-500">Line total</Label>
                        <div className="h-8 px-2 flex items-center text-sm font-semibold text-slate-900 bg-slate-50 rounded-md border border-slate-200">
                          ${lineTotal(it).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2">
                <div>
                  <Label className="text-[11px] text-slate-500">Discount (USD)</Label>
                  <Input type="number" min="0" step="1" className="h-8 text-sm" value={discount} onChange={(e) => setDiscount(e.target.value)} />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Tax / VAT rate (%)</Label>
                  <Input type="number" min="0" step="0.5" className="h-8 text-sm" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-md p-2.5 text-sm space-y-1">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                {discountNum > 0 && <div className="flex justify-between text-slate-600"><span>Discount</span><span>- ${discountNum.toFixed(2)}</span></div>}
                <div className="flex justify-between text-slate-600"><span>Tax ({taxNum}%)</span><span>${taxAmount.toFixed(2)}</span></div>
                <div className="flex justify-between text-base font-bold text-slate-900 pt-1 border-t border-slate-200"><span>Total due</span><span>${grandTotal.toFixed(2)}</span></div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1">
                <Label>Client billing address (for compliance)</Label>
                <Textarea
                  rows={3}
                  placeholder="Legal name, street, city, postcode, country"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label>Client Tax ID / VAT #</Label>
                  <Input value={clientTaxId} onChange={(e) => setClientTaxId(e.target.value)} placeholder="Optional" />
                </div>
                <div className="space-y-1">
                  <Label>PO / Reference</Label>
                  <Input value={purchaseOrder} onChange={(e) => setPurchaseOrder(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Payment terms</Label>
                <Textarea
                  rows={3}
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>External invoice link (optional — Binance Pay, Stripe, hosted invoice)</Label>
              <Input
                placeholder="https://…"
                value={invoiceUrl}
                onChange={(e) => setInvoiceUrl(e.target.value)}
              />
            </div>

            {invoiceUrl && (
              <a
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3 h-3" /> Preview external invoice link
              </a>
            )}


            <div className="space-y-2">
              <Label>Scope / approval notes (visible to client)</Label>
              <Textarea
                rows={3}
                placeholder="What's included, timeline, any conditions…"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Decline reason (visible to client if declined)</Label>
              <Textarea
                rows={3}
                placeholder="Explain what we can't do and how the client can adjust the brief…"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
              />
            </div>

            <ManualCryptoAdminPanel briefId={brief.id} onVerified={onSaved} />

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              <p><strong>Current status:</strong> {STATUS_META[brief.review_status]?.label || brief.review_status}</p>
              <p className="mt-1">Email the client manually to notify them — they will also see the update inside their dashboard.</p>
              <a
                href={`mailto:${brief.email}?subject=${encodeURIComponent('Your AI Business Launch Kit brief — ' + brief.project_name)}`}
                className="inline-flex items-center gap-1.5 text-blue-600 hover:underline mt-2"
              >
                <Mail className="w-3 h-3" /> Email {brief.email}
              </a>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            variant="outline"
            className="border-rose-200 text-rose-700 hover:bg-rose-50"
            onClick={handleDecline}
            disabled={!!saving}
          >
            {saving === 'decline' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
            Decline
          </Button>
          {brief.review_status === 'approved' && (
            <Button
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={handleMarkPaid}
              disabled={!!saving}
            >
              {saving === 'production' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
              Mark as In Production
            </Button>
          )}
          <Button
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
            onClick={handleApprove}
            disabled={!!saving}
          >
            {saving === 'approve' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Approve &amp; Send Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManualCryptoAdminPanel = ({ briefId, onVerified }: { briefId: string; onVerified: () => void }) => {
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = async () => {
    const { data } = await supabase
      .from('manual_crypto_payments')
      .select('*')
      .eq('brief_id', briefId)
      .order('created_at', { ascending: false });
    setPayments(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [briefId]);

  const act = async (id: string, status: 'verified' | 'rejected') => {
    setActingId(id);
    try {
      const patch: any = {
        status,
        admin_notes: notes[id] || null,
        verified_at: status === 'verified' ? new Date().toISOString() : null,
      };
      const { error } = await supabase.from('manual_crypto_payments').update(patch).eq('id', id);
      if (error) throw error;
      if (status === 'verified') {
        await supabase
          .from('launch_kit_briefs')
          .update({ review_status: 'paid', payment_method: 'manual_crypto' })
          .eq('id', briefId);
        toast.success('Verified — brief moved to In Production');
        onVerified();
      } else {
        toast.success('Marked as rejected — client will see your note');
        await load();
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed');
    } finally {
      setActingId(null);
    }
  };

  if (loading) return null;
  if (payments.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-3">
      <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Manual crypto payments ({payments.length})</p>
      {payments.map((p) => (
        <div key={p.id} className="bg-white rounded-md border border-slate-200 p-3 text-xs space-y-2">
          <div className="flex justify-between gap-2 flex-wrap">
            <span className="font-mono font-semibold">{p.order_id}</span>
            <span className={`px-2 py-0.5 rounded-full font-semibold ${
              p.status === 'verified' ? 'bg-green-100 text-green-700' :
              p.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
              p.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
              'bg-amber-100 text-amber-700'
            }`}>{p.status}</span>
          </div>
          <div className="text-slate-600">
            ${Number(p.amount_usd).toLocaleString()} · {p.network}
          </div>
          <div className="font-mono text-[10px] break-all text-slate-500">to {p.wallet_address}</div>
          {p.tx_hash && (
            <div>
              <p className="text-slate-500 font-semibold">TX:</p>
              <p className="font-mono text-[10px] break-all">{p.tx_hash}</p>
            </div>
          )}
          {p.status === 'submitted' && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <Input
                placeholder="Notes (shown to client if rejected)"
                value={notes[p.id] || ''}
                onChange={(e) => setNotes({ ...notes, [p.id]: e.target.value })}
                className="text-xs h-8"
              />
              <div className="flex gap-2">
                <Button size="sm" disabled={actingId === p.id} onClick={() => act(p.id, 'verified')} className="flex-1 bg-green-600 hover:bg-green-700 text-white h-8 text-xs">
                  {actingId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Verify & Mark Paid
                </Button>
                <Button size="sm" variant="outline" disabled={actingId === p.id} onClick={() => act(p.id, 'rejected')} className="flex-1 border-rose-200 text-rose-700 h-8 text-xs">
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminLaunchKitBriefs;
